import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pagesService, seoService } from '../../../lib/services';

const SCHEMA_TEMPLATES = {
  WebPage: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '',
    description: '',
    url: '',
    datePublished: '',
    dateModified: ''
  },
  Article: {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '',
    description: '',
    url: '',
    datePublished: '',
    dateModified: '',
    author: { '@type': 'Person', name: '' },
    publisher: { '@type': 'Organization', name: '', logo: { '@type': 'ImageObject', url: '' } }
  },
  Organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '',
    url: '',
    logo: '',
    description: '',
    contactPoint: { '@type': 'ContactPoint', telephone: '', contactType: 'customer service' }
  },
  BreadcrumbList: {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: '' },
      { '@type': 'ListItem', position: 2, name: '', item: '' }
    ]
  },
  FAQPage: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Question 1?', acceptedAnswer: { '@type': 'Answer', text: 'Answer 1' } },
      { '@type': 'Question', name: 'Question 2?', acceptedAnswer: { '@type': 'Answer', text: 'Answer 2' } }
    ]
  },
  LocalBusiness: {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: '',
    description: '',
    url: '',
    telephone: '',
    address: { '@type': 'PostalAddress', streetAddress: '', addressLocality: '', addressRegion: '', postalCode: '', addressCountry: '' }
  },
  Product: {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: '',
    description: '',
    image: '',
    offers: { '@type': 'Offer', price: '', priceCurrency: 'USD', availability: 'https://schema.org/InStock' }
  }
};

const SchemaMarkup = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [schemaType, setSchemaType] = useState('WebPage');
  const [schemaJson, setSchemaJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingSchema, setLoadingSchema] = useState(false);

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadPageSchema(selectedPage?.id);
    }
  }, [selectedPage]);

  const loadPages = async () => {
    setLoading(true);
    const { data } = await pagesService?.getAll();
    setPages(data || []);
    setLoading(false);
  };

  const loadPageSchema = async (pageId) => {
    setLoadingSchema(true);
    const { data } = await seoService?.getByPageId(pageId);
    if (data?.structuredData) {
      const parsed = data?.structuredData;
      setSchemaJson(JSON.stringify(parsed, null, 2));
      if (parsed?.['@type']) setSchemaType(parsed?.['@type']);
    } else {
      applyTemplate(schemaType);
    }
    setJsonError('');
    setLoadingSchema(false);
  };

  const applyTemplate = (type) => {
    const template = SCHEMA_TEMPLATES?.[type] || SCHEMA_TEMPLATES?.WebPage;
    const filled = { ...template };
    if (selectedPage) {
      const siteUrl = window.location?.origin;
      const slug = selectedPage?.slug === 'home' ? '' : `/${selectedPage?.slug}`;
      if (filled?.name !== undefined) filled.name = selectedPage?.title || '';
      if (filled?.headline !== undefined) filled.headline = selectedPage?.title || '';
      if (filled?.url !== undefined) filled.url = `${siteUrl}${slug}`;
      if (filled?.datePublished !== undefined) filled.datePublished = selectedPage?.publishDate || selectedPage?.createdAt || '';
      if (filled?.dateModified !== undefined) filled.dateModified = selectedPage?.updatedAt || '';
    }
    setSchemaJson(JSON.stringify(filled, null, 2));
    setJsonError('');
  };

  const handleTypeChange = (type) => {
    setSchemaType(type);
    applyTemplate(type);
  };

  const handleJsonChange = (value) => {
    setSchemaJson(value);
    setSaved(false);
    try {
      JSON.parse(value);
      setJsonError('');
    } catch (e) {
      setJsonError(e?.message);
    }
  };

  const handleSave = async () => {
    if (jsonError || !selectedPage) return;
    setSaving(true);
    try {
      const parsed = JSON.parse(schemaJson);
      await seoService?.upsert(selectedPage?.id, { structuredData: parsed });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setJsonError('Invalid JSON: ' + e?.message);
    }
    setSaving(false);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(
      `<script type="application/ld+json">\n${schemaJson}\n</script>`
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Icon name="Code2" size={20} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Schema Markup</h2>
            <p className="text-sm text-gray-600">Add structured data (JSON-LD) per page for rich search results</p>
          </div>
        </div>
        <Button
          variant="primary"
          iconName={saved ? 'Check' : 'Save'}
          iconPosition="left"
          onClick={handleSave}
          disabled={saving || !selectedPage || !!jsonError}
        >
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Schema'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Page selector + type */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Page</label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                Loading pages...
              </div>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                {pages?.map(page => (
                  <button
                    key={page?.id}
                    onClick={() => setSelectedPage(page)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      selectedPage?.id === page?.id
                        ? 'bg-indigo-50 text-indigo-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium truncate">{page?.title}</div>
                    <div className="text-xs text-gray-400 truncate">/{page?.slug}</div>
                  </button>
                ))}
                {pages?.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-400 text-center">No pages found</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Schema Type</label>
            <div className="space-y-1">
              {Object.keys(SCHEMA_TEMPLATES)?.map(type => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    schemaType === type
                      ? 'bg-indigo-600 text-white' :'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: JSON editor */}
        <div className="lg:col-span-2 space-y-3">
          {!selectedPage && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Icon name="AlertCircle" size={16} className="text-amber-500" />
              <span className="text-sm text-amber-700">Select a page to edit its schema markup</span>
            </div>
          )}

          {loadingSchema ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-sm text-gray-500">Loading schema...</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-mono">application/ld+json</span>
                  {selectedPage && (
                    <span className="text-xs text-indigo-400">— {selectedPage?.title}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {jsonError ? (
                    <span className="text-xs text-red-400 flex items-center gap-1">
                      <Icon name="X" size={12} /> Invalid JSON
                    </span>
                  ) : (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Icon name="Check" size={12} /> Valid JSON
                    </span>
                  )}
                  <button onClick={handleCopy} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                    <Icon name="Copy" size={12} /> Copy HTML
                  </button>
                </div>
              </div>
              <textarea
                value={schemaJson}
                onChange={e => handleJsonChange(e?.target?.value)}
                rows={20}
                disabled={!selectedPage}
                className={`w-full px-4 py-3 bg-gray-900 text-green-400 font-mono text-xs border-0 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none ${!selectedPage ? 'opacity-50 cursor-not-allowed' : ''}`}
                spellCheck={false}
              />
              {jsonError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-600 font-mono">{jsonError}</span>
                </div>
              )}
            </>
          )}

          <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
            <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              Schema markup helps search engines understand your content and can enable rich results like star ratings, FAQs, and breadcrumbs in Google Search.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchemaMarkup;
